import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  operationsSummaries: {},
  operationsSummaryUIFlags: {},
  approvalWorkflowUIFlags: {},
  canaryApprovalUIFlags: {},
  deliveryReconciliationUIFlags: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
};

const defaultOperationsSummaryUIFlags = {
  isFetching: false,
  hasError: false,
};

const defaultApprovalWorkflowUIFlags = {
  isUpdating: false,
  hasError: false,
};

const defaultCanaryApprovalUIFlags = {
  isUpdating: false,
  hasError: false,
};

const defaultDeliveryReconciliationUIFlags = {
  isUpdating: false,
  hasError: false,
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      return filteredRecords.sort((a1, a2) => a1.id - a2.id);
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsChannelTypes);
  },
  getWhatsAppCampaigns: _state => {
    return _state.records
      .filter(record => {
        return (
          record.campaign_type === CAMPAIGN_TYPES.ONE_OFF &&
          (record.whatsapp_web ||
            record.inbox?.channel_type === INBOX_TYPES.WHATSAPP)
        );
      })
      .sort((a1, a2) => a1.id - a2.id);
  },
  getEmailCampaigns: (_state, _getters) => {
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, [INBOX_TYPES.EMAIL]);
  },
  getInfojobsCampaigns: _state => {
    return _state.records
      .filter(
        record =>
          record.campaign_type === CAMPAIGN_TYPES.ONE_OFF && record.infojobs
      )
      .sort((a1, a2) => a1.id - a2.id);
  },
  getLiveChatCampaigns: (_state, _getters) => {
    const liveChatChannelTypes = [INBOX_TYPES.WEB];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONGOING, liveChatChannelTypes);
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
  getOperationsSummary: _state => campaignId => {
    return _state.operationsSummaries[campaignId] || null;
  },
  getOperationsSummaryUIFlags: _state => campaignId => {
    return {
      ...defaultOperationsSummaryUIFlags,
      ...(_state.operationsSummaryUIFlags[campaignId] || {}),
    };
  },
  getApprovalWorkflowUIFlags: _state => campaignId => {
    return {
      ...defaultApprovalWorkflowUIFlags,
      ...(_state.approvalWorkflowUIFlags[campaignId] || {}),
    };
  },
  getCanaryApprovalUIFlags: _state => campaignId => {
    return {
      ...defaultCanaryApprovalUIFlags,
      ...(_state.canaryApprovalUIFlags[campaignId] || {}),
    };
  },
  getDeliveryReconciliationUIFlags: _state => campaignId => {
    return {
      ...defaultDeliveryReconciliationUIFlags,
      ...(_state.deliveryReconciliationUIFlags[campaignId] || {}),
    };
  },
};

export const actions = {
  get: async function getCampaigns({ commit }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get();
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      // No catch on purpose: the original error propagates untouched. The
      // previous `catch (error) { throw new Error(error) }` stringified it and
      // dropped `error.response`, so every caller's `error.response.data.error`
      // was undefined and the server's real validation message never showed.
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  previewAiMessages: async (_context, payload) => {
    const { data } = await CampaignsAPI.previewAiMessages(payload);
    return data;
  },
  getOperationsSummary: async ({ commit }, campaignId) => {
    commit(types.SET_CAMPAIGN_OPERATIONS_SUMMARY_UI_FLAG, {
      campaignId,
      uiFlags: { isFetching: true, hasError: false },
    });

    try {
      const response = await CampaignsAPI.operationsSummary(campaignId);
      commit(types.SET_CAMPAIGN_OPERATIONS_SUMMARY, {
        campaignId,
        summary: response.data,
      });
      return response.data;
    } catch (error) {
      commit(types.SET_CAMPAIGN_OPERATIONS_SUMMARY_UI_FLAG, {
        campaignId,
        uiFlags: { hasError: true },
      });
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_OPERATIONS_SUMMARY_UI_FLAG, {
        campaignId,
        uiFlags: { isFetching: false },
      });
    }
  },
  runApprovalWorkflow: async (
    { commit },
    { campaignId, approvalAction, stageKey, reason }
  ) => {
    commit(types.SET_CAMPAIGN_APPROVAL_WORKFLOW_UI_FLAG, {
      campaignId,
      uiFlags: { isUpdating: true, hasError: false },
    });

    try {
      const workflowPayload = {
        approval_action: approvalAction,
        ...(stageKey ? { stage_key: stageKey } : {}),
        ...(reason ? { reason } : {}),
      };
      const response = await CampaignsAPI.approvalWorkflow(
        campaignId,
        workflowPayload
      );
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      commit(types.SET_CAMPAIGN_APPROVAL_WORKFLOW_UI_FLAG, {
        campaignId,
        uiFlags: { hasError: true },
      });
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_APPROVAL_WORKFLOW_UI_FLAG, {
        campaignId,
        uiFlags: { isUpdating: false },
      });
    }
  },
  approveCanary: async ({ commit }, { campaignId, reason }) => {
    commit(types.SET_CAMPAIGN_CANARY_APPROVAL_UI_FLAG, {
      campaignId,
      uiFlags: { isUpdating: true, hasError: false },
    });

    try {
      const response = await CampaignsAPI.approveCanary(campaignId, {
        ...(reason ? { reason } : {}),
      });
      commit(types.EDIT_CAMPAIGN, response.data);
      return response.data;
    } catch (error) {
      commit(types.SET_CAMPAIGN_CANARY_APPROVAL_UI_FLAG, {
        campaignId,
        uiFlags: { hasError: true },
      });
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_CANARY_APPROVAL_UI_FLAG, {
        campaignId,
        uiFlags: { isUpdating: false },
      });
    }
  },
  reconcileDelivery: async (
    { commit },
    {
      campaignId,
      deliveryId,
      state: reconciliationState,
      providerMessageId,
      failureClassification,
      errorCode,
      reason,
      retryAfterReconciliation,
      evidence,
    }
  ) => {
    commit(types.SET_CAMPAIGN_DELIVERY_RECONCILIATION_UI_FLAG, {
      campaignId,
      uiFlags: { isUpdating: true, hasError: false },
    });

    try {
      const response = await CampaignsAPI.reconcileDelivery(campaignId, {
        delivery_id: deliveryId,
        state: reconciliationState,
        ...(providerMessageId
          ? { provider_message_id: providerMessageId }
          : {}),
        ...(failureClassification
          ? { failure_classification: failureClassification }
          : {}),
        ...(errorCode ? { error_code: errorCode } : {}),
        ...(reason ? { reason } : {}),
        ...(retryAfterReconciliation
          ? { retry_after_reconciliation: true }
          : {}),
        ...(evidence ? { evidence } : {}),
      });
      return response.data;
    } catch (error) {
      commit(types.SET_CAMPAIGN_DELIVERY_RECONCILIATION_UI_FLAG, {
        campaignId,
        uiFlags: { hasError: true },
      });
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_DELIVERY_RECONCILIATION_UI_FLAG, {
        campaignId,
        uiFlags: { isUpdating: false },
      });
    }
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
  [types.SET_CAMPAIGN_OPERATIONS_SUMMARY](_state, { campaignId, summary }) {
    _state.operationsSummaries = {
      ..._state.operationsSummaries,
      [campaignId]: summary,
    };
  },
  [types.SET_CAMPAIGN_OPERATIONS_SUMMARY_UI_FLAG](
    _state,
    { campaignId, uiFlags }
  ) {
    _state.operationsSummaryUIFlags = {
      ..._state.operationsSummaryUIFlags,
      [campaignId]: {
        ...(_state.operationsSummaryUIFlags[campaignId] || {}),
        ...uiFlags,
      },
    };
  },
  [types.SET_CAMPAIGN_APPROVAL_WORKFLOW_UI_FLAG](
    _state,
    { campaignId, uiFlags }
  ) {
    _state.approvalWorkflowUIFlags = {
      ..._state.approvalWorkflowUIFlags,
      [campaignId]: {
        ...(_state.approvalWorkflowUIFlags[campaignId] || {}),
        ...uiFlags,
      },
    };
  },
  [types.SET_CAMPAIGN_CANARY_APPROVAL_UI_FLAG](
    _state,
    { campaignId, uiFlags }
  ) {
    _state.canaryApprovalUIFlags = {
      ..._state.canaryApprovalUIFlags,
      [campaignId]: {
        ...(_state.canaryApprovalUIFlags[campaignId] || {}),
        ...uiFlags,
      },
    };
  },
  [types.SET_CAMPAIGN_DELIVERY_RECONCILIATION_UI_FLAG](
    _state,
    { campaignId, uiFlags }
  ) {
    _state.deliveryReconciliationUIFlags = {
      ..._state.deliveryReconciliationUIFlags,
      [campaignId]: {
        ...(_state.deliveryReconciliationUIFlags[campaignId] || {}),
        ...uiFlags,
      },
    };
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
